import React, { useState, useEffect, useRef, useCallback } from 'react'

// Libraries
import { Col, Container, Row } from 'react-bootstrap'
import { useParams, useNavigate, Link } from 'react-router-dom'

// Components
import PortfolioOverlay from '../Components/Portfolio/PortfolioOverlay'
import RDPHeader from '../Components/Header/RDPHeader'
import RDPFooter from '../Components/Footers/RDPFooter'

// Connexion Supabase
import { supabase } from '../lib/supabaseClient'

// Constantes pour l'infinite scroll
const PRODUCTS_PER_PAGE = 24
const LOAD_MORE_THRESHOLD = 200

// Slugify pour comparaison
const slugify = (str) =>
  str
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')

const RangePage = (props) => {
  const { slug } = useParams()
  const navigate = useNavigate()

  const [products, setProducts] = useState([])
  const [rangeName, setRangeName] = useState('')
  const [loading, setLoading] = useState(true)

  // Infinite scroll states
  const [displayCount, setDisplayCount] = useState(PRODUCTS_PER_PAGE)
  const [loadingMore, setLoadingMore] = useState(false)
  const loaderRef = useRef(null)

  useEffect(() => {
    window.scrollTo(0, 0)
    fetchRangeProducts()
    setDisplayCount(PRODUCTS_PER_PAGE)
  }, [slug])

  // Infinite scroll - IntersectionObserver
  const loadMoreProducts = useCallback(() => {
    setLoadingMore(true)
    setTimeout(() => {
      setDisplayCount(prev => prev + PRODUCTS_PER_PAGE)
      setLoadingMore(false)
    }, 300)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0]
        if (target.isIntersecting && !loadingMore && !loading) {
          loadMoreProducts()
        }
      },
      {
        rootMargin: `${LOAD_MORE_THRESHOLD}px`,
        threshold: 0
      }
    )

    if (loaderRef.current) {
      observer.observe(loaderRef.current)
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current)
      }
    }
  }, [loadMoreProducts, loadingMore, loading])

  const fetchRangeProducts = async () => {
    try {
      setLoading(true)

      // Récupérer tous les produits avec une gamme
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .not('range', 'is', null)

      if (error) throw error

      // Filtrer par slug de la gamme
      const rangeProducts = data.filter(p => slugify(p.range) === slug)

      if (rangeProducts.length === 0) {
        // Aucun produit trouvé pour cette gamme - redirection
        navigate('/produits/tout')
        return
      }

      // Récupérer le nom réel de la gamme
      setRangeName(rangeProducts[0].range)

      // Trier par 1) capacité nominale croissante, 2) hauteur croissante
      rangeProducts.sort((a, b) => {
        const capA = a.nominal_capacity || 0
        const capB = b.nominal_capacity || 0
        if (capA !== capB) return capA - capB
        const heightA = parseFloat(a.height) || 0
        const heightB = parseFloat(b.height) || 0
        return heightA - heightB
      })

      // Formater pour PortfolioOverlay
      const formattedData = rangeProducts.map(product => ({
        id: product.id,
        title: product.name,
        img: product.image_url || 'https://placehold.co/800x650/cccccc/333333',
        category: product.categories || (product.category ? [product.category] : []),
        subtitle: `${product.capacity || ''} ml - ${product.cap || ''}`.trim(),
        link: `/produit/${product.slug || product.id}`,
        double_col: false,
        productData: product
      }))

      setProducts(formattedData)
    } catch (err) {
      console.error('Erreur lors du chargement de la gamme:', err)
      navigate('/produits/tout')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={props.style}>
      {/* Header */}
      <RDPHeader {...props} />

      {/* Hero Section */}
      <section className="pt-[130px] pb-[40px] lg:pt-[110px] lg:pb-[30px] md:pt-[100px] md:pb-[25px] bg-gradient-to-b from-glass-pearl to-white">
        <Container>
          <Row className="justify-center">
            <Col lg={8} className="text-center">
              <h1 className="font-serif font-semibold text-glass-darkest mb-[10px] text-[3rem] lg:text-[2.5rem] md:text-[2rem]">
                Gamme {rangeName || '...'}
              </h1>
              <p className="text-glass-medium text-lg">
                {products.length} produit{products.length > 1 ? 's' : ''} dans cette gamme
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section produits */}
      <section className="py-[60px] lg:py-[50px] md:py-[40px] bg-gradient-to-b from-white to-glass-pearl">
        <Container fluid className="px-[20px] max-w-[1600px]">
          {(() => {
            const displayedProducts = products.slice(0, displayCount)
            const hasMoreProducts = displayCount < products.length

            if (loading) {
              return (
                <div className="text-center py-20">
                  <div style={{
                    display: 'inline-block',
                    width: '40px',
                    height: '40px',
                    border: '4px solid #E5E7EB',
                    borderTop: '4px solid #3B82F6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <p className="text-lg text-glass-dark mt-4">Chargement des produits...</p>
                </div>
              )
            }

            if (products.length > 0) {
              return (
                <>
                  <PortfolioOverlay
                    overlay={["#3B82F6cc", "#60A5FAcc"]}
                    grid="grid grid-4col xl-grid-4col lg-grid-4col md-grid-3col sm-grid-2col xs-grid-1col gutter-large"
                    filterData={undefined}
                    data={displayedProducts}
                    hideRangeIndicator={true}
                  />

                  {/* Infinite scroll trigger & loader */}
                  {hasMoreProducts && (
                    <div ref={loaderRef} className="text-center py-10">
                      {loadingMore ? (
                        <div className="inline-flex items-center gap-3">
                          <div style={{
                            display: 'inline-block',
                            width: '24px',
                            height: '24px',
                            border: '3px solid #E5E7EB',
                            borderTop: '3px solid #3B82F6',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                          <span className="text-glass-medium text-sm">Chargement...</span>
                        </div>
                      ) : (
                        <span className="text-glass-medium text-sm">Scroll pour voir plus</span>
                      )}
                    </div>
                  )}

                  {/* Compteur de produits affichés */}
                  <div className="text-center py-4 border-t border-glass-border mt-6">
                    <p className="text-sm text-glass-medium mb-0">
                      <span className="font-bold text-glass-accent">{displayedProducts.length}</span> sur <span className="font-bold">{products.length}</span> produits affichés
                    </p>
                  </div>
                </>
              )
            }

            return (
              <div className="text-center py-20">
                <p className="text-lg text-glass-dark">Aucun produit trouvé dans cette gamme.</p>
              </div>
            )
          })()}

          {/* Bouton retour */}
          <div className="text-center mt-[50px]">
            <Link
              to="/produits/tout"
              className="inline-flex items-center gap-2 px-[30px] py-[12px] bg-glass-accent text-white rounded-[8px] hover:bg-glass-darkest transition-all font-semibold text-sm"
            >
              <i className="fas fa-arrow-left"></i>
              Retour aux produits
            </Link>
          </div>
        </Container>
      </section>

      {/* Style pour animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Footer */}
      <RDPFooter />
    </div>
  )
}

export default RangePage
